// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Group,
  List,
  Loader,
  Select,
  Skeleton,
  Space,
  Stack,
  Text,
} from '@mantine/core';
import { i18n } from '@lingui/core';
import { closeAllModals } from '@mantine/modals';
import { FC, useCallback, useEffect, useState } from 'react';
import { IconBallpen, IconChevronDown, IconSend, IconTemplate } from '@tabler/icons';
import { RecommendationS } from '../../../../Service/RecommendationS';
import { RecommendationDto } from '../../../../Types/RecommendationT';
import { Contact, ContactS } from '../../../../Service/ContactS';
import { BaseInput, RichInput } from '../../../../Organismns/tiptap/TiptapEditorInput';
import BaseToolbar from '../../../../Organismns/tiptap/toolbars/BaseToolbar';
import TemplateMessageToolbar from '../../../../Organismns/tiptap/toolbars/TemplateMessageToolbar';
import { DataS } from '../../../../Service/DataS';
import { useAgencyTemplates } from '../../../../Service/hooks/useAgencyTemplates';
import { AgencyTemplateDTO, EmailContent } from '../../../../Types/MessageT';
import { openCreateTemplateModal } from '../../../../Templates/Settings/Emails/agency/AgencyTemplateModals';
import { PlaceholderS } from '../../../../Service/PlaceholderS';
import { useJwtClaims } from '../../../../Service/Context/AuthContext';
import AttachmentsAlert from './AttachmentsAlert';
import { TiptapS } from '../../../../Service/TiptapS';
import { LicenceRestriction, LicenceS } from '../../../../Service/LicenceS';

interface Props {
  recommendations: RecommendationDto[];
  reload: () => void;
  eventId: number;
  executeWithLicenceCheck: (licenceRestriction: LicenceRestriction, featureHandler: () => unknown) => void;
}

const CheckAllAvailabilityModal: FC<Props> = ({ recommendations, reload, eventId, executeWithLicenceCheck }) => {
  const [loading, setLoading] = useState(false);
  const { templates } = useAgencyTemplates();
  const { profileId } = useJwtClaims();
  const [selectedTemplate, selectTemplate] = useState<AgencyTemplateDTO>();
  const [customSubject, setCustomSubject] = useState(false);
  const [selectedRecommendations, setRecommendations] = useState(recommendations.map((r) => r.id));
  const { length } = selectedRecommendations;
  const [showAttachmentsAlert, setShowAttachmentsAlert] = useState(false);

  const [mail, setMail] = useState<EmailContent>({ subject: '', message: '' });
  const { subject, message } = mail;

  const updateProp = (patch: Partial<EmailContent>) => setMail((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    if (selectedTemplate) {
      const handler = () => {
        setMail(selectedTemplate);
        if (selectedTemplate.attachments.length > 0) {
          setShowAttachmentsAlert(true);
        } else {
          setShowAttachmentsAlert(false);
        }
        if (!customSubject) {
          setCustomSubject(true);
        }
      };
      executeWithLicenceCheck(LicenceS.Restrictions.AGENCY_TEMPLATES, handler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const checkAvailability = async () => {
    setLoading(true);
    const placeholdersResolved = await PlaceholderS.checkPlaceholders({
      profileId,
      eventId,
      message,
      subject,
    });
    if (placeholdersResolved) {
      await RecommendationS.checkAvailabilities({
        recommendationsIds: selectedRecommendations,
        ...mail,
        newVersion: true,
      });
      closeAllModals();
      reload();
    }
    setLoading(false);
  };

  const toggle = useCallback(
    (recommendationId: string) => {
      const remove = (id: string) => setRecommendations(selectedRecommendations.filter((_id) => _id !== id));
      const append = (id: string) => setRecommendations([...selectedRecommendations, id]);
      if (selectedRecommendations.includes(recommendationId)) {
        remove(recommendationId);
      } else {
        append(recommendationId);
      }
    },
    [selectedRecommendations],
  );

  const mapContactItemUI = (recommendation: RecommendationDto) => {
    const { id, recommendedContactId } = recommendation;
    return (
      <ContactListItem
        key={recommendation.id}
        selected={selectedRecommendations.includes(id)}
        toggle={() => toggle(id)}
        contactId={recommendedContactId}
      />
    );
  };

  return (
    <>
      <Text>So funktionierts:</Text>
      <List mt="md" mb="xl" type="ordered" size="xs" spacing={5}>
        <List.Item>Es wird eine E-Mail an den Dienstleister gesendet.</List.Item>
        <List.Item>Du wirst benachrichtigt, sobald der Dienstleister geantwortet hat.</List.Item>
        <List.Item>Ist er an dem Tag verfügbar, kannst Du ihm die Event-Infos übermitteln.</List.Item>
      </List>
      <Text mb="sm">Folgende Dienstleister werden per E-Mail informiert:</Text>
      <Box ml="sm">{recommendations.map(mapContactItemUI)}</Box>
      <Divider my="sm" />
      {templates.length > 0 && (
        <Select
          mb="xs"
          label={
            <Text size="xs" color="dimmed">
              Vorlage
            </Text>
          }
          icon={<IconTemplate size={20} />}
          data={DataS.getAgencyTemplateOptions(templates)}
          value={selectedTemplate ? selectedTemplate.id : undefined}
          onChange={(val) => selectTemplate(templates.find((t) => t.id === val))}
          styles={{ rightSection: { pointerEvents: 'none' } }}
          rightSection={<IconChevronDown size={14} />}
          rightSectionWidth={25}
        />
      )}
      {showAttachmentsAlert && <AttachmentsAlert />}
      {!customSubject && (
        <Group position="apart">
          <Stack spacing={0}>
            <Text mb={3} size={12} color="dimmed">
              Bereff
            </Text>
            <Text>
              Verfügbar am <span className="mention">Vermittlungsdatum</span>
            </Text>
          </Stack>
          <ActionIcon>
            <IconBallpen size={20} onClick={() => setCustomSubject(true)} />
          </ActionIcon>
        </Group>
      )}
      {customSubject && (
        <BaseInput
          withUpdate
          toolbar={<BaseToolbar />}
          label="Bereff"
          value={subject}
          onUpdate={(html) => updateProp({ subject: html })}
        />
      )}
      <Space h="sm" />
      <Text mb={3} size={12} color="dimmed">
        Nachricht
      </Text>
      <Text mb="sm">
        Hey <span className="mention">Dienstleister-Name</span>,
      </Text>
      <Text mb="md">
        ich möchte Dich für ein Event am <span className="mention">Vermittlungsdatum</span> vermitteln - bist Du noch
        verfügbar?
      </Text>
      <RichInput
        withUpdate
        styles={TiptapS.TextAreaStyles}
        label="Nachricht an den Dienstleister"
        value={message}
        onUpdate={(html) => updateProp({ message: html })}
        toolbar={
          <TemplateMessageToolbar
            saveAsTemplate={() => openCreateTemplateModal({ templateDto: { subject, message, attachments: [] } })}
          />
        }
      />
      <Center mt="xl">
        <Button
          mb="xs"
          onClick={checkAvailability}
          disabled={loading || length === 0}
          leftIcon={!loading ? <IconSend size={16} /> : <Loader size="xs" />}
          sx={{ width: 250 }}
        >
          {i18n._('recommendation.check.availability')}
        </Button>
      </Center>
    </>
  );
};

interface ListItemProps {
  contactId: number;
  selected: boolean;
  toggle: () => void;
}

const ContactListItem: FC<ListItemProps> = ({ contactId, selected, toggle }) => {
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState<Contact>();

  useEffect(() => {
    setLoading(true);
    ContactS.fetchById(contactId)
      .then((_contact) => _contact && setContact(_contact))
      .finally(() => setLoading(false));
  }, [contactId]);

  if (loading) {
    return <Skeleton mb="sm" height={30} width={250} />;
  }
  if (!contact) {
    return null;
  }
  const { personDescription, email } = contact;

  return <Checkbox size="xs" label={personDescription ?? email} checked={selected} onChange={toggle} />;
};

export default CheckAllAvailabilityModal;
